/**
 * src/components/ui/Select.jsx — labelled dropdown.
 *
 * A real native `<select>`, not a custom-widget reimplementation — per
 * REDESIGN.md's own instruction to prefer native elements wherever they can
 * do the job, and because a native select gets the OS picker UI for free on
 * mobile (a scroll wheel on iOS, a full-screen list on Android), which a
 * custom listbox has to rebuild by hand to match.
 *
 * modern-web-guidance's `branded-select-styling` guide covers the new
 * "Customizable <select>" API (`appearance: base-select`, `::picker(select)`)
 * for fully theming the open dropdown itself, not just the closed control.
 * Deliberately NOT used here: it has no listed Baseline status yet (Chrome/
 * Edge only as of the guide's own browser table) — below this project's bar
 * for using a feature without feature-detection and graceful degradation,
 * and a form control is exactly the wrong place to have materially
 * different behaviour across browsers. This styles only the closed control
 * (border, radius, a CSS-drawn chevron) via plain, Baseline-safe CSS, and
 * lets every browser render its own native open-state picker.
 */
import { Field, syncAriaInvalid } from './Field.jsx';

/**
 * @param {{
 *   label: string,
 *   hint?: React.ReactNode,
 *   error?: string|null,
 *   required?: boolean,
 *   id?: string,
 *   children: React.ReactNode,
 * } & React.SelectHTMLAttributes<HTMLSelectElement>} props
 * @returns {JSX.Element}
 */
export function Select({ label, hint, error, required, id, onBlur, onInput, children, ...selectProps }) {
  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={id}>
      {({ inputId, hintId, errorId }) => (
        <select
          id={inputId}
          required={required}
          aria-describedby={hintId}
          aria-errormessage={error ? errorId : undefined}
          aria-invalid={error ? 'true' : undefined}
          onBlur={(e) => {
            syncAriaInvalid(e);
            onBlur?.(e);
          }}
          onInput={(e) => {
            syncAriaInvalid(e);
            onInput?.(e);
          }}
          {...selectProps}
        >
          {children}
        </select>
      )}
    </Field>
  );
}
